import { useState } from 'react';
import { DiscardAsk, useDismissGuard } from '../components/dismissGuard.tsx';
import { Link } from 'react-router-dom';
import { useDuplicates, useMerge } from '../api/contacts.ts';
import { useMe } from '../api/queries.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { ContactSheet } from '../components/contacts/ContactSheet.tsx';
import { field, label, primary, secondary } from '../components/contacts/Sheet.tsx';

/**
 * `/contacts/duplicates` — possible duplicate people, for tiers 1-2 (Q5): pairs sharing a phone number or
 * an email, or with very similar names. Merging keeps one, moves every link to it with a version, and
 * retires the other (never deleted), with a reason. Two people can share a name, so nothing here is merged
 * without a person deciding.
 */
export function ContactDuplicates() {
  const me = useMe();
  const tier = me.data?.role.tier ?? 99;
  const q = useDuplicates(tier <= 2);
  const [open, setOpen] = useState<string | null>(null);
  const [merging, setMerging] = useState<{ keep: string; retire: string; keepName: string; retireName: string } | null>(null);
  if (me.data && tier > 2) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-6">
        <h1 className="title-bar">Possible duplicate contacts</h1>
        <p className="mt-2 text-neutral-700">Merging contacts is open to tiers 1-2.</p>
      </div>
    );
  }
  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-4xl px-4 py-6">
        <h1 className="title-bar">Possible duplicate contacts</h1>
        <p className="mt-1 text-neutral-600">
          The same phone number or email, or a very similar name. Open each to compare; merge only the same person.
        </p>
        {q.error && !q.data ? (
          <LoadFailure what="The list" error={q.error} onRetry={() => void q.refetch()} retrying={q.isFetching} />
        ) : !q.data ? (
          <p className="py-6 text-neutral-600">Loading…</p>
        ) : q.data.pairs.length === 0 ? (
          <p className="mt-4 text-neutral-700">No likely duplicates.</p>
        ) : (
          <ul className="mt-4 divide-y divide-neutral-200 rounded-lg border border-neutral-200 bg-white">
            {q.data.pairs.map((p) => (
              <li key={`${p.a_id}-${p.b_id}`} className="flex flex-wrap items-center gap-2 px-3 py-2">
                <span className="min-w-0 flex-1">
                  <button type="button" className="min-h-[44px] font-semibold underline" onClick={() => setOpen(p.a_id)}>{p.a_name}</button>
                  {' and '}
                  <button type="button" className="min-h-[44px] font-semibold underline" onClick={() => setOpen(p.b_id)}>{p.b_name}</button>
                  <span className="block text-sm text-neutral-600">Same {p.matched_on.join(' and ')}</span>
                </span>
                <button type="button" className={secondary}
                        onClick={() => setMerging({ keep: p.a_id, retire: p.b_id, keepName: p.a_name, retireName: p.b_name })}>
                  Merge…
                </button>
              </li>
            ))}
          </ul>
        )}
        <Link to="/clients" className="mt-4 inline-flex min-h-[44px] items-center underline">Back to clients</Link>
      </div>
      {open && <ContactSheet contactId={open} linkId="" onClose={() => setOpen(null)} />}
      {merging && <MergeForm m={merging} swap={() => setMerging({ keep: merging.retire, retire: merging.keep,
        keepName: merging.retireName, retireName: merging.keepName })} done={() => setMerging(null)} />}
    </div>
  );
}

function MergeForm({ m, swap, done }: {
  m: { keep: string; retire: string; keepName: string; retireName: string }; swap: () => void; done: () => void;
}) {
  const merge = useMerge();
  const [why, setWhy] = useState('');
  // A-#199 2: a reason typed is never discarded silently by Cancel (no backdrop and no Escape here)
  const guard = useDismissGuard(done);
  return (
    <div role="dialog" aria-labelledby="merge-title" onInput={guard.changed}
         className="fixed inset-x-0 bottom-0 border-t border-neutral-300 bg-panel px-4 py-3 shadow-xl">
      <DiscardAsk guard={guard} />
      <div className="mx-auto max-w-4xl">
        <h2 id="merge-title" className="font-semibold text-neutral-900">Keep {m.keepName}; retire {m.retireName}</h2>
        <p className="text-sm text-neutral-700">Every link of the one retired moves to the one kept. The retired record stays, findable by tiers 1-2.</p>
        <button type="button" className={`${secondary} mt-2`} onClick={swap}>Keep the other one instead</button>
        <label htmlFor="merge-why" className={`${label} mt-2`}>Why they are the same person</label>
        <input id="merge-why" className={field} value={why} onChange={(e) => setWhy(e.target.value)} maxLength={2000} />
        {merge.error && <p role="alert" className="mt-2 text-warning-fg">{merge.error.message}</p>}
        <div className="mt-2 flex gap-2">
          <button type="button" className={primary} disabled={!why.trim() || merge.isPending}
                  onClick={() => merge.mutate({ survivor_id: m.keep, retire_id: m.retire, reason_text: why.trim() }, { onSuccess: done })}>
            {merge.isPending ? 'Merging…' : 'Merge'}
          </button>
          <button type="button" className={secondary} onClick={guard.request}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
